import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Animals & Wildlife — 8 ANIMATED templates
   butterfly_lifecycle, fish_anatomy, dinosaur_skeleton,
   bird_flight, whale_dive, ant_colony, frog_metamorphosis,
   spider_web
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Butterfly Lifecycle ──────────────────────────────── */
export const ButterflyLifecycle: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Butterfly Life Cycle";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const stages = (props.stages as string[]) || ["Egg", "Caterpillar", "Chrysalis", "Butterfly"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Circular path */}
      <circle cx="400" cy="280" r="170" fill="none" stroke="#2c3e50" strokeWidth="2" strokeDasharray="8,6" />
      {/* Stages */}
      {[
        { cx: 400, cy: 110, icon: "🥚" },
        { cx: 570, cy: 280, icon: "🐛" },
        { cx: 400, cy: 450, icon: "🫘" },
        { cx: 230, cy: 280, icon: "🦋" },
      ].map((s, i) => (
        <g key={i}>
          <circle cx={s.cx} cy={s.cy} r={i === activeIndex ? 50 : 42}
            fill={i === activeIndex ? "#1c2833" : "#0d1117"}
            stroke={i === activeIndex ? "#f9a825" : "#546e7a"} strokeWidth={i === activeIndex ? 3 : 1.5}>
            {i === activeIndex && (
              <animate attributeName="r" values="48;54;48" dur="1.2s" repeatCount="indefinite" />
            )}
          </circle>
          <text x={s.cx} y={s.cy + 8} textAnchor="middle" fontSize="28">{s.icon}</text>
          <text x={s.cx} y={s.cy + (i < 2 ? -55 : 65)} textAnchor="middle"
            fill={i === activeIndex ? "#f9a825" : "#bdc3c7"} fontSize="13" fontWeight="600">{stages[i]}</text>
        </g>
      ))}
      {/* Animated arrows between stages */}
      {[
        "M440,130 Q540,160 560,240",
        "M560,320 Q540,400 440,430",
        "M360,430 Q260,400 240,320",
        "M240,240 Q260,160 360,130",
      ].map((d, i) => (
        <path key={i} d={d} fill="none" stroke="#546e7a" strokeWidth="2" markerEnd="url(#bl-arr)">
          <animate attributeName="stroke-dashoffset" from="20" to="0" dur="1s" repeatCount="indefinite" />
        </path>
      ))}
      <defs>
        <marker id="bl-arr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,0 L10,5 L0,10Z" fill="#546e7a" />
        </marker>
      </defs>
      <text x="400" y="550" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Fish Anatomy ─────────────────────────────────────── */
export const FishAnatomy: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Fish Anatomy";
  const highlightTarget = (props.highlightTarget as string) || "";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Water bubbles animated */}
      {[120, 300, 550, 680].map((x, i) => (
        <circle key={i} cx={x} cy={450 - i * 60} r={4 + i * 2} fill="#42a5f5" opacity="0.15">
          <animate attributeName="cy" from={450 - i * 60} to={50} dur={`${3 + i}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.15;0.05;0.15" dur={`${3 + i}s`} repeatCount="indefinite" />
        </circle>
      ))}
      {/* Fish body */}
      <ellipse cx="400" cy="280" rx="200" ry="90" fill="#1565c0" stroke="#0d47a1" strokeWidth="2">
        <animate attributeName="ry" values="90;95;90" dur="2s" repeatCount="indefinite" />
      </ellipse>
      {/* Tail */}
      <polygon points="600,280 680,220 680,340" fill="#0d47a1">
        <animateTransform attributeName="transform" type="rotate" values="-5,600,280;5,600,280;-5,600,280" dur="1s" repeatCount="indefinite" />
      </polygon>
      {/* Eye */}
      <circle cx="280" cy="260" r="18" fill="#ecf0f1" stroke={highlightTarget === "eye" ? "#f9a825" : "#0d47a1"} strokeWidth="2" />
      <circle cx="280" cy="260" r="8" fill="#0d1117" />
      {/* Dorsal fin */}
      <path d="M350,190 Q400,100 450,190" fill={highlightTarget === "dorsal" ? "#f9a825" : "#1976d2"} stroke="#0d47a1" strokeWidth="1.5">
        <animateTransform attributeName="transform" type="rotate" values="-3,400,190;3,400,190;-3,400,190" dur="1.5s" repeatCount="indefinite" />
      </path>
      {/* Pectoral fin */}
      <ellipse cx="340" cy="320" rx="40" ry="15" fill={highlightTarget === "pectoral" ? "#f9a825" : "#1976d2"} opacity="0.8">
        <animateTransform attributeName="transform" type="rotate" values="-10,340,320;10,340,320;-10,340,320" dur="1.2s" repeatCount="indefinite" />
      </ellipse>
      {/* Gills */}
      {[0, 15, 30].map((dy) => (
        <line key={dy} x1="310" y1={250 + dy} x2="310" y2={260 + dy}
          stroke={highlightTarget === "gills" ? "#f9a825" : "#0d47a1"} strokeWidth="2">
          <animate attributeName="x1" values="310;312;310" dur="1.5s" repeatCount="indefinite" />
        </line>
      ))}
      {/* Scales pattern */}
      {Array.from({ length: 6 }, (_, r) =>
        Array.from({ length: 4 }, (_, c) => (
          <circle key={`${r}-${c}`} cx={340 + r * 35} cy={255 + c * 20} r="8" fill="none" stroke="#42a5f5" strokeWidth="0.5" opacity="0.3" />
        ))
      )}
      {/* Labels */}
      {[
        { x: 280, y: 225, t: "Eye", id: "eye" },
        { x: 400, y: 85, t: "Dorsal Fin", id: "dorsal" },
        { x: 340, y: 355, t: "Pectoral Fin", id: "pectoral" },
        { x: 310, y: 240, t: "Gills", id: "gills" },
        { x: 660, y: 210, t: "Tail", id: "tail" },
      ].map((l) => (
        <text key={l.id} x={l.x} y={l.y} textAnchor="middle" fill={highlightTarget === l.id ? "#f9a825" : "#7f8c8d"} fontSize="11">{l.t}</text>
      ))}
      <text x="400" y="480" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Dinosaur Skeleton ────────────────────────────────── */
export const DinosaurSkeleton: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Dinosaur Skeleton";
  const highlightTarget = (props.highlightTarget as string) || "";
  const animationProgress = (props.animationProgress as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Ground line */}
      <line x1="80" y1="460" x2="720" y2="460" stroke="#3e2723" strokeWidth="3" />
      {/* Spine — animated slight breathing */}
      <path d="M200,300 Q300,280 400,290 Q500,300 580,320"
        fill="none" stroke={highlightTarget === "spine" ? "#f9a825" : "#eceff1"} strokeWidth="4"
        strokeDasharray={animationProgress > 0 ? "none" : "1000"} strokeDashoffset={1000 - animationProgress * 1000} />
      {/* Skull */}
      <ellipse cx="180" cy="260" rx="55" ry="35"
        fill="none" stroke={highlightTarget === "skull" ? "#f9a825" : "#eceff1"} strokeWidth="3" />
      <circle cx="165" cy="250" r="8" fill="#0d1117" stroke="#eceff1" strokeWidth="2" />
      {/* Jaw */}
      <path d="M150,280 Q170,310 210,290" fill="none" stroke="#eceff1" strokeWidth="2.5">
        <animateTransform attributeName="transform" type="rotate" values="0,180,280;-5,180,280;0,180,280" dur="3s" repeatCount="indefinite" />
      </path>
      {/* Teeth */}
      {[155, 170, 185, 200].map((x) => (
        <line key={x} x1={x} y1="278" x2={x + 3} y2="290" stroke="#eceff1" strokeWidth="1.5" />
      ))}
      {/* Ribs */}
      {[280, 320, 360, 400, 440].map((x, i) => (
        <path key={i} d={`M${x},${285 + i * 3} Q${x + 10},${340 + i * 5} ${x - 5},${380 + i * 5}`}
          fill="none" stroke={highlightTarget === "ribs" ? "#f9a825" : "#e0e0e0"} strokeWidth="2" opacity={0.9 - i * 0.08}>
          <animate attributeName="opacity" values={`${0.9 - i * 0.08};${0.7 - i * 0.08};${0.9 - i * 0.08}`} dur="2.5s" repeatCount="indefinite" />
        </path>
      ))}
      {/* Tail */}
      <path d="M580,320 Q620,310 660,330 Q700,350 720,340"
        fill="none" stroke={highlightTarget === "tail" ? "#f9a825" : "#eceff1"} strokeWidth="3">
        <animateTransform attributeName="transform" type="rotate" values="0,580,320;3,580,320;0,580,320;-3,580,320;0,580,320" dur="4s" repeatCount="indefinite" />
      </path>
      {/* Legs */}
      {[
        { x: 300, d: "M300,380 L290,460", id: "front_leg" },
        { x: 340, d: "M340,380 L350,460", id: "front_leg" },
        { x: 480, d: "M480,390 L470,460", id: "hind_leg" },
        { x: 520, d: "M520,390 L540,460", id: "hind_leg" },
      ].map((l, i) => (
        <path key={i} d={l.d} fill="none" stroke={highlightTarget === l.id ? "#f9a825" : "#e0e0e0"} strokeWidth="3" />
      ))}
      {/* Labels */}
      {[
        { x: 180, y: 215, t: "Skull" }, { x: 400, y: 265, t: "Spine" },
        { x: 350, y: 400, t: "Ribs" }, { x: 680, y: 325, t: "Tail" },
      ].map((l, i) => (
        <text key={i} x={l.x} y={l.y} textAnchor="middle" fill="#7f8c8d" fontSize="11">{l.t}</text>
      ))}
      <text x="400" y="520" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Bird Flight ──────────────────────────────────────── */
export const BirdFlight: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Bird Flight";
  const phase = (props.phase as number) || 0;
  const wingAngle = Math.sin(phase * 2) * 30;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="bf-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0d47a1" /><stop offset="100%" stopColor="#42a5f5" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#bf-sky)" rx="16" />
      {/* Clouds animated */}
      {[
        { x: 100, y: 100, s: 1 }, { x: 500, y: 150, s: 0.7 }, { x: 300, y: 80, s: 0.5 },
      ].map((c, i) => (
        <g key={i} opacity="0.3">
          <ellipse cx={c.x} cy={c.y} rx={80 * c.s} ry={30 * c.s} fill="#ecf0f1">
            <animate attributeName="cx" from={c.x} to={c.x + 100} dur={`${8 + i * 3}s`} repeatCount="indefinite" />
          </ellipse>
        </g>
      ))}
      {/* Bird body */}
      <g transform={`translate(400, ${280 + Math.sin(phase) * 15})`}>
        {/* Body */}
        <ellipse cx="0" cy="0" rx="45" ry="18" fill="#5d4037" />
        {/* Head */}
        <circle cx="-50" cy="-8" r="14" fill="#4e342e" />
        <circle cx="-56" cy="-12" r="3" fill="#fff" />
        {/* Beak */}
        <polygon points="-65,-8 -75,-5 -65,-3" fill="#f9a825" />
        {/* Left wing */}
        <g transform={`rotate(${-wingAngle}, -10, -15)`}>
          <path d="M-10,-15 Q-80,-80 -150,-30 Q-90,-10 -10,-15" fill="#6d4c41" />
        </g>
        {/* Right wing */}
        <g transform={`rotate(${wingAngle}, -10, 15)`}>
          <path d="M-10,15 Q-80,80 -150,30 Q-90,10 -10,15" fill="#6d4c41" />
        </g>
        {/* Tail */}
        <polygon points="45,-5 80,-15 80,15 45,5" fill="#5d4037" />
      </g>
      {/* Lift/Drag arrows */}
      <g opacity="0.6">
        <line x1="400" y1={240 + Math.sin(phase) * 15} x2="400" y2={180 + Math.sin(phase) * 15}
          stroke="#4caf50" strokeWidth="3" markerEnd="url(#bf-up)" />
        <text x="420" y={210 + Math.sin(phase) * 15} fill="#4caf50" fontSize="12">Lift</text>
        <line x1="400" y1={330 + Math.sin(phase) * 15} x2="400" y2={380 + Math.sin(phase) * 15}
          stroke="#e74c3c" strokeWidth="3" markerEnd="url(#bf-down)" />
        <text x="420" y={370 + Math.sin(phase) * 15} fill="#e74c3c" fontSize="12">Weight</text>
      </g>
      <defs>
        <marker id="bf-up" viewBox="0 0 10 10" refX="5" refY="10" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,10 L5,0 L10,10Z" fill="#4caf50" />
        </marker>
        <marker id="bf-down" viewBox="0 0 10 10" refX="5" refY="0" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M0,0 L5,10 L10,0Z" fill="#e74c3c" />
        </marker>
      </defs>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Whale Dive ───────────────────────────────────────── */
export const WhaleDive: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Whale Dive";
  const animationProgress = (props.animationProgress as number) || 0;
  const depth = animationProgress * 400;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="wd-ocean" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#42a5f5" /><stop offset="30%" stopColor="#1565c0" />
          <stop offset="70%" stopColor="#0d47a1" /><stop offset="100%" stopColor="#0a1628" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#wd-ocean)" rx="16" />
      {/* Depth markers */}
      {[0, 100, 200, 300, 400].map((d) => (
        <g key={d}>
          <line x1="700" y1={80 + d * 1.1} x2="720" y2={80 + d * 1.1} stroke="#546e7a" strokeWidth="1" />
          <text x="730" y={84 + d * 1.1} fill="#546e7a" fontSize="10">{d}m</text>
        </g>
      ))}
      {/* Whale */}
      <g transform={`translate(350, ${80 + depth * 1.1}) rotate(${animationProgress * 30})`}>
        <ellipse cx="0" cy="0" rx="80" ry="30" fill="#37474f" />
        <path d="M-80,0 Q-90,-25 -110,-15 Q-105,0 -110,15 Q-90,25 -80,0" fill="#455a64" />
        <circle cx="55" cy="-10" r="5" fill="#0d1117" />
        {/* Belly */}
        <ellipse cx="0" cy="10" rx="60" ry="15" fill="#546e7a" />
        {/* Fins animated */}
        <ellipse cx="20" cy="-28" rx="25" ry="8" fill="#455a64">
          <animateTransform attributeName="transform" type="rotate" values="-10,20,-28;10,20,-28;-10,20,-28" dur="2s" repeatCount="indefinite" />
        </ellipse>
      </g>
      {/* Bubbles trail */}
      {[0, 1, 2, 3, 4].map((i) => (
        <circle key={i} cx={360 + i * 15} cy={60 + depth * 1.1 - 40 - i * 20} r={3 + i}
          fill="#90caf9" opacity={0.3 - i * 0.05}>
          <animate attributeName="cy" from={60 + depth * 1.1 - 40 - i * 20} to={40} dur={`${2 + i}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.3;0" dur={`${2 + i}s`} repeatCount="indefinite" />
        </circle>
      ))}
      {/* Surface waves */}
      <path d="M0,70 Q100,60 200,70 Q300,80 400,70 Q500,60 600,70 Q700,80 800,70" fill="none" stroke="#90caf9" strokeWidth="2" opacity="0.3">
        <animate attributeName="d" values="M0,70 Q100,60 200,70 Q300,80 400,70 Q500,60 600,70 Q700,80 800,70;M0,70 Q100,80 200,70 Q300,60 400,70 Q500,80 600,70 Q700,60 800,70;M0,70 Q100,60 200,70 Q300,80 400,70 Q500,60 600,70 Q700,80 800,70" dur="3s" repeatCount="indefinite" />
      </path>
      <text x="400" y="570" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Ant Colony ───────────────────────────────────────── */
export const AntColony: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Ant Colony";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const chambers = (props.chambers as string[]) || ["Queen Chamber", "Nursery", "Food Storage", "Waste"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Surface */}
      <rect x="0" y="0" width="800" height="100" fill="#2e7d32" opacity="0.3" rx="16" />
      <line x1="0" y1="100" x2="800" y2="100" stroke="#4e342e" strokeWidth="3" />
      {/* Entry tunnel */}
      <rect x="390" y="80" width="20" height="60" fill="#3e2723" />
      {/* Tunnels */}
      <path d="M400,140 L400,200 L250,260" fill="none" stroke="#5d4037" strokeWidth="15" strokeLinecap="round" />
      <path d="M400,200 L550,260" fill="none" stroke="#5d4037" strokeWidth="15" strokeLinecap="round" />
      <path d="M250,260 L200,360" fill="none" stroke="#5d4037" strokeWidth="12" strokeLinecap="round" />
      <path d="M250,260 L350,360" fill="none" stroke="#5d4037" strokeWidth="12" strokeLinecap="round" />
      <path d="M550,260 L600,360" fill="none" stroke="#5d4037" strokeWidth="12" strokeLinecap="round" />
      {/* Chambers */}
      {[
        { cx: 200, cy: 400, r: 50 },
        { cx: 350, cy: 400, r: 45 },
        { cx: 550, cy: 300, r: 45 },
        { cx: 600, cy: 420, r: 40 },
      ].map((ch, i) => (
        <g key={i}>
          <ellipse cx={ch.cx} cy={ch.cy} rx={ch.r} ry={ch.r * 0.7}
            fill={i === activeIndex ? "#5d4037" : "#3e2723"}
            stroke={i === activeIndex ? "#f9a825" : "#5d4037"} strokeWidth={i === activeIndex ? 2 : 1}>
            {i === activeIndex && <animate attributeName="rx" values={`${ch.r};${ch.r + 4};${ch.r}`} dur="1.5s" repeatCount="indefinite" />}
          </ellipse>
          <text x={ch.cx} y={ch.cy + 4} textAnchor="middle" fill="#bdc3c7" fontSize="10">{chambers[i]}</text>
        </g>
      ))}
      {/* Animated ants walking */}
      {[0, 1, 2, 3, 4].map((i) => (
        <circle key={i} cx="0" cy="0" r="3" fill="#1a1a1a">
          <animateMotion dur={`${3 + i}s`} repeatCount="indefinite" begin={`${i * 0.7}s`}
            path="M400,140 L400,200 L250,260 L200,360 L200,400" />
        </circle>
      ))}
      {[0, 1, 2].map((i) => (
        <circle key={`r${i}`} cx="0" cy="0" r="3" fill="#2c2c2c">
          <animateMotion dur={`${4 + i}s`} repeatCount="indefinite" begin={`${i * 1.2}s`}
            path="M200,400 L200,360 L250,260 L400,200 L400,140" />
        </circle>
      ))}
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Frog Metamorphosis ───────────────────────────────── */
export const FrogMetamorphosis: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Frog Life Cycle";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const stages = (props.stages as string[]) || ["Eggs", "Tadpole", "Tadpole with Legs", "Froglet", "Adult Frog"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Water bottom area */}
      <rect x="0" y="420" width="800" height="180" fill="#0d47a1" opacity="0.15" rx="0" />
      {/* Stages in arc */}
      {[
        { x: 100, y: 300, icon: "⚪", sz: 24 },
        { x: 250, y: 180, icon: "🐟", sz: 28 },
        { x: 400, y: 130, icon: "🐸", sz: 24 },
        { x: 550, y: 180, icon: "🐸", sz: 28 },
        { x: 700, y: 300, icon: "🐸", sz: 36 },
      ].map((s, i) => (
        <g key={i}>
          <rect x={s.x - 45} y={s.y - 35} width="90" height="70" rx="12"
            fill={i === activeIndex ? "#1c2833" : "#0d1117"}
            stroke={i === activeIndex ? "#4caf50" : "#2c3e50"} strokeWidth={i === activeIndex ? 2 : 1}>
            {i === activeIndex && (
              <animate attributeName="stroke-width" values="2;4;2" dur="1s" repeatCount="indefinite" />
            )}
          </rect>
          <text x={s.x} y={s.y + 8} textAnchor="middle" fontSize={s.sz}>{s.icon}</text>
          <text x={s.x} y={s.y + 52} textAnchor="middle" fill={i === activeIndex ? "#4caf50" : "#7f8c8d"} fontSize="11">{stages[i]}</text>
        </g>
      ))}
      {/* Connecting arrows */}
      {[175, 325, 475, 625].map((x, i) => (
        <line key={i} x1={x - 20} y1={240 - (i < 2 ? i * 40 : (3 - i) * 40)} x2={x + 20} y2={240 - (i < 2 ? (i + 1) * 40 : (2 - i) * 40)}
          stroke="#4caf50" strokeWidth="2" markerEnd="url(#fm-arr)" opacity="0.5">
          <animate attributeName="opacity" values="0.3;0.7;0.3" dur="2s" repeatCount="indefinite" begin={`${i * 0.3}s`} />
        </line>
      ))}
      <defs>
        <marker id="fm-arr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto">
          <path d="M0,0 L10,5 L0,10Z" fill="#4caf50" />
        </marker>
      </defs>
      {/* Animated water ripples */}
      <ellipse cx="400" cy="430" rx="300" ry="10" fill="none" stroke="#42a5f5" strokeWidth="1" opacity="0.2">
        <animate attributeName="rx" values="300;350;300" dur="3s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.2;0.05;0.2" dur="3s" repeatCount="indefinite" />
      </ellipse>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Spider Web ───────────────────────────────────────── */
export const SpiderWeb: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Spider Web";
  const animationProgress = (props.animationProgress as number) || 0;
  const cx = 400, cy = 280;
  const rings = 6;
  const spokes = 12;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Spokes */}
      {Array.from({ length: spokes }, (_, i) => {
        const angle = (i * 360 / spokes) * Math.PI / 180;
        const endR = 200;
        return (
          <line key={i} x1={cx} y1={cy}
            x2={cx + Math.cos(angle) * endR} y2={cy + Math.sin(angle) * endR}
            stroke="#bdbdbd" strokeWidth="1" opacity="0.4">
            <animate attributeName="opacity" values="0.2;0.5;0.2" dur={`${2 + i * 0.2}s`} repeatCount="indefinite" />
          </line>
        );
      })}
      {/* Spiral rings */}
      {Array.from({ length: rings }, (_, r) => {
        const radius = 30 + r * 30;
        const revealR = animationProgress * rings;
        if (r > revealR) return null;
        const points = Array.from({ length: spokes + 1 }, (_, i) => {
          const angle = (i * 360 / spokes) * Math.PI / 180;
          return `${cx + Math.cos(angle) * radius},${cy + Math.sin(angle) * radius}`;
        }).join(" ");
        return (
          <polyline key={r} points={points} fill="none" stroke="#e0e0e0" strokeWidth="0.8" opacity={0.3 + r * 0.08}>
            <animate attributeName="opacity" values={`${0.3 + r * 0.08};${0.15 + r * 0.04};${0.3 + r * 0.08}`} dur="4s" repeatCount="indefinite" />
          </polyline>
        );
      })}
      {/* Spider in center */}
      <circle cx={cx} cy={cy} r="8" fill="#1a1a1a" stroke="#bdbdbd" strokeWidth="1">
        <animate attributeName="r" values="8;9;8" dur="2s" repeatCount="indefinite" />
      </circle>
      {/* Spider legs */}
      {Array.from({ length: 8 }, (_, i) => {
        const angle = (i * 45) * Math.PI / 180;
        return (
          <line key={i} x1={cx + Math.cos(angle) * 8} y1={cy + Math.sin(angle) * 8}
            x2={cx + Math.cos(angle) * 22} y2={cy + Math.sin(angle) * 22}
            stroke="#bdbdbd" strokeWidth="1.5">
            <animateTransform attributeName="transform" type="rotate" values={`0,${cx},${cy};${i % 2 === 0 ? 3 : -3},${cx},${cy};0,${cx},${cy}`} dur="1.5s" repeatCount="indefinite" />
          </line>
        );
      })}
      {/* Dew drops */}
      {[60, 120, 180, 240, 300].map((deg, i) => {
        const r = 90 + i * 20;
        const rad = deg * Math.PI / 180;
        return (
          <circle key={i} cx={cx + Math.cos(rad) * r} cy={cy + Math.sin(rad) * r} r="3" fill="#42a5f5" opacity="0.5">
            <animate attributeName="opacity" values="0.5;0.2;0.5" dur={`${1.5 + i * 0.3}s`} repeatCount="indefinite" />
          </circle>
        );
      })}
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
